import React, { useMemo, useState } from 'react';
import { Button, Input, List, Select } from 'antd';
import type { Dispatch, SetStateAction } from 'react';
import {
  DEFAULT_PARKS_DATA_FILTER,
  type Park,
  type ParksDataFilter,
  type ParksListSort,
} from '../../types';
import {
  COMPREHENSIVE_ENERGY_CONSUMPTION_OPTIONS,
  GREEN_ELECTRICITY_SCALE_OPTIONS,
  NATIONAL_DOCKING_OPTIONS,
  NATIONAL_PROJECT_TYPE_OPTIONS,
  PENDING_GROSS_REGIONAL_PRODUCT_OPTIONS,
  PROVINCIAL_LEADING_INDUSTRY_OPTIONS,
} from '../../constants/parkFilters';
import { countParksByLevel } from '../../utils/helpers';

/** 侧边栏 Props */
export interface SidebarProps {
  parks: Park[];
  filter: ParksDataFilter;
  setFilter: Dispatch<SetStateAction<ParksDataFilter>>;
  selectedParkId: string | null | undefined;
  onSelectPark: (parkId: string) => void;
}

const panelClass = 'dash-panel dash-panel--left';

function sortParksByAdministrativeDivision(parks: Park[]): Park[] {
  return [...parks].sort((a, b) => {
    const p = (a.province || '').localeCompare(b.province || '', 'zh-CN');
    if (p !== 0) return p;
    const c = (a.city || '').localeCompare(b.city || '', 'zh-CN');
    if (c !== 0) return c;
    const d = (a.district || '').localeCompare(b.district || '', 'zh-CN');
    if (d !== 0) return d;
    return (a.name || '').localeCompare(b.name || '', 'zh-CN');
  });
}

const ENERGY_CONSUMPTION_RANK = new Map<string, number>(
  COMPREHENSIVE_ENERGY_CONSUMPTION_OPTIONS.map((value, index) => [value, index]),
);

const GREEN_ELECTRICITY_RANK = new Map<string, number>(
  GREEN_ELECTRICITY_SCALE_OPTIONS.map((value, index) => [value, index]),
);

function sortProvincialLikeParks(
  parks: Park[],
  sortBy: ParksListSort,
): Park[] {
  const administrativeOrder = sortParksByAdministrativeDivision(parks);
  if (sortBy === 'administrative') return administrativeOrder;

  const fallbackOrder = new Map(
    administrativeOrder.map((park, index) => [park.id, index]),
  );
  const isEnergySort = sortBy === 'energy-asc' || sortBy === 'energy-desc';
  const isGreenElectricitySort =
    sortBy === 'green-electricity-asc' || sortBy === 'green-electricity-desc';
  const isGdpSort = sortBy === 'gdp-asc' || sortBy === 'gdp-desc';
  const direction = sortBy.endsWith('-desc') ? -1 : 1;

  return [...administrativeOrder].sort((a, b) => {
    const resolveRank = (park: Park): number | undefined => {
      if (park.parkLevel === 'national') return undefined;
      if (isEnergySort) {
        return ENERGY_CONSUMPTION_RANK.get(park.comprehensiveEnergyConsumption);
      }
      if (isGreenElectricitySort) {
        return GREEN_ELECTRICITY_RANK.get(park.greenElectricityScale);
      }
      if (isGdpSort && park.parkLevel === 'pending') {
        return park.grossRegionalProduct != null && park.grossRegionalProduct > 0
          ? park.grossRegionalProduct
          : undefined;
      }
      return undefined;
    };
    const aRank = resolveRank(a);
    const bRank = resolveRank(b);

    if (aRank == null && bRank != null) return 1;
    if (aRank != null && bRank == null) return -1;
    if (aRank != null && bRank != null && aRank !== bRank) {
      return (aRank - bRank) * direction;
    }
    return (fallbackOrder.get(a.id) ?? 0) - (fallbackOrder.get(b.id) ?? 0);
  });
}

function getParkSortValue(park: Park, sortBy: ParksListSort): string | undefined {
  if (park.parkLevel === 'national') return undefined;
  if (sortBy === 'energy-asc' || sortBy === 'energy-desc') {
    return park.comprehensiveEnergyConsumption;
  }
  if (sortBy === 'green-electricity-asc' || sortBy === 'green-electricity-desc') {
    return park.greenElectricityScale;
  }
  if ((sortBy === 'gdp-asc' || sortBy === 'gdp-desc') && park.parkLevel === 'pending') {
    return park.grossRegionalProduct != null && park.grossRegionalProduct > 0
      ? `${park.grossRegionalProduct.toLocaleString('zh-CN')}亿元`
      : '暂无可核验值';
  }
  return undefined;
}

type SortField = 'energy' | 'green-electricity' | 'gdp';

function nextSort(current: ParksListSort, field: SortField): ParksListSort {
  if (current === `${field}-desc`) return `${field}-asc`;
  if (current === `${field}-asc`) return 'administrative';
  return `${field}-desc`;
}

function sortButtonLabel(current: ParksListSort, field: SortField): string {
  if (current === `${field}-asc`) return '升序';
  if (current === `${field}-desc`) return '降序';
  return '排序';
}

/**
 * 左侧边栏：统计、筛选、列表（大屏半透明浮层）
 */
export function Sidebar(props: SidebarProps): React.ReactElement {
  const { parks, filter, setFilter, selectedParkId, onSelectPark } = props;

  const sortedParks = useMemo(
    () => sortProvincialLikeParks(parks, filter.sortBy),
    [parks, filter.sortBy],
  );
  const [keywordInput, setKeywordInput] = useState('');

  const levelSelectValue = filter.level;
  const showProvincialLikeSecondaryFilters =
    filter.level === 'provincial' || filter.level === 'pending';
  const keywordFilteredParks = useMemo(() => {
    const keyword = keywordInput.trim().toLowerCase();
    if (!keyword) return sortedParks;
    return sortedParks.filter((park) => {
      const text = [park.name, park.province, park.city, park.district]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return text.includes(keyword);
    });
  }, [keywordInput, sortedParks]);

  const statistics = useMemo(
    () => countParksByLevel(keywordFilteredParks),
    [keywordFilteredParks],
  );

  return (
    <aside className={panelClass}>
      <div className="dash-section">
        <div className="dash-stat-grid">
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">国家级园区数量</div>
            <div className="dash-stat-card__value">{statistics.national}</div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">省级园区数量</div>
            <div className="dash-stat-card__value dash-stat-card__value--accent">
              {statistics.provincial}
            </div>
          </div>
          <div className="dash-stat-card">
            <div className="dash-stat-card__label">待开发园区数量</div>
            <div className="dash-stat-card__value dash-stat-card__value--pending">
              {statistics.pending}
            </div>
          </div>
        </div>
      </div>

      <div className="dash-section">
        <div className="dash-field-row">
          <div className="dash-field-label">园区搜索</div>
          <Button
            type="link"
            size="small"
            className="dash-inline-link-btn"
            onClick={() => {
              setFilter(DEFAULT_PARKS_DATA_FILTER);
              setKeywordInput('');
            }}
          >
            重置筛选
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            allowClear
            value={keywordInput}
            placeholder="请输入园区关键字"
            onChange={(e) => setKeywordInput(e.target.value)}
          />
          <Button
            type="default"
            disabled={!keywordInput.trim()}
            onClick={() => setKeywordInput('')}
          >
            清空
          </Button>
        </div>
        <div className="dash-field-label">园区层级</div>
        <Select
          className="dash-select"
          dropdownClassName="dash-select-dropdown"
          style={{ width: '100%' }}
          value={levelSelectValue}
          onChange={(v) => {
            setFilter({ ...DEFAULT_PARKS_DATA_FILTER, level: v });
          }}
          options={[
            { label: '全部', value: 'all' },
            { label: '国家级', value: 'national' },
            { label: '省级', value: 'provincial' },
            { label: '待开发', value: 'pending' },
          ]}
        />

        {filter.level === 'national' && (
          <>
            <div className="dash-field-label dash-field-label--spaced">
              可建设落地项目类型
            </div>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              placeholder="可多选"
              className="dash-select"
              dropdownClassName="dash-select-dropdown"
              style={{ width: '100%' }}
              value={filter.nationalProjectTypes}
              onChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  nationalProjectTypes: v.length ? v : undefined,
                }))
              }
              options={NATIONAL_PROJECT_TYPE_OPTIONS.map((t) => ({
                label: t,
                value: t,
              }))}
            />
            <div className="dash-field-label dash-field-label--spaced">对接情况</div>
            <Select
              allowClear
              placeholder="请选择"
              className="dash-select"
              dropdownClassName="dash-select-dropdown"
              style={{ width: '100%' }}
              value={filter.nationalDocking}
              onChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  nationalDocking: v ?? undefined,
                }))
              }
              options={[...NATIONAL_DOCKING_OPTIONS]}
            />
          </>
        )}

        {showProvincialLikeSecondaryFilters && (
          <>
            <div className="dash-field-label dash-field-label--spaced">
              综合能源消费量
            </div>
            <div className="dash-filter-with-sort">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="请选择"
                className="dash-select"
                dropdownClassName="dash-select-dropdown"
                value={filter.comprehensiveEnergyConsumption}
                onChange={(v) =>
                  setFilter((prev) => ({
                    ...prev,
                    comprehensiveEnergyConsumption: v ?? undefined,
                  }))
                }
                options={COMPREHENSIVE_ENERGY_CONSUMPTION_OPTIONS.map((t) => ({
                  label: t,
                  value: t,
                }))}
              />
              <Button
                className="dash-sort-btn"
                type={filter.sortBy.startsWith('energy-') ? 'primary' : 'default'}
                onClick={() =>
                  setFilter((prev) => ({
                    ...prev,
                    sortBy: nextSort(prev.sortBy, 'energy'),
                  }))
                }
              >
                {sortButtonLabel(filter.sortBy, 'energy')}
              </Button>
            </div>
            <div className="dash-field-label dash-field-label--spaced">
              绿电资源规模
            </div>
            <div className="dash-filter-with-sort">
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                placeholder="可多选"
                className="dash-select"
                dropdownClassName="dash-select-dropdown"
                value={filter.greenElectricityScales}
                onChange={(v) =>
                  setFilter((prev) => ({
                    ...prev,
                    greenElectricityScales: v.length ? v : undefined,
                  }))
                }
                options={GREEN_ELECTRICITY_SCALE_OPTIONS.map((t) => ({
                  label: t,
                  value: t,
                }))}
              />
              <Button
                className="dash-sort-btn"
                type={filter.sortBy.startsWith('green-electricity-') ? 'primary' : 'default'}
                onClick={() =>
                  setFilter((prev) => ({
                    ...prev,
                    sortBy: nextSort(prev.sortBy, 'green-electricity'),
                  }))
                }
              >
                {sortButtonLabel(filter.sortBy, 'green-electricity')}
              </Button>
            </div>
            {filter.level === 'pending' && (
              <>
                <div className="dash-field-label dash-field-label--spaced">
                  园区地区生产总值
                </div>
                <div className="dash-filter-with-sort">
                  <Select
                    allowClear
                    placeholder="请选择有官方数值的 GDP 档位"
                    className="dash-select"
                    dropdownClassName="dash-select-dropdown"
                    value={filter.pendingGrossRegionalProduct}
                    onChange={(v) =>
                      setFilter((prev) => ({
                        ...prev,
                        pendingGrossRegionalProduct: v ?? undefined,
                      }))
                    }
                    options={PENDING_GROSS_REGIONAL_PRODUCT_OPTIONS.map((t) => ({
                      label: t,
                      value: t,
                    }))}
                  />
                  <Button
                    className="dash-sort-btn"
                    type={filter.sortBy.startsWith('gdp-') ? 'primary' : 'default'}
                    onClick={() =>
                      setFilter((prev) => ({
                        ...prev,
                        sortBy: nextSort(prev.sortBy, 'gdp'),
                      }))
                    }
                  >
                    {sortButtonLabel(filter.sortBy, 'gdp')}
                  </Button>
                </div>
              </>
            )}
            <div className="dash-field-label dash-field-label--spaced">
              主导产业类型
            </div>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              placeholder="可多选"
              className="dash-select"
              dropdownClassName="dash-select-dropdown"
              style={{ width: '100%' }}
              value={filter.provincialLeadingIndustries}
              onChange={(v) =>
                setFilter((prev) => ({
                  ...prev,
                  provincialLeadingIndustries: v.length ? v : undefined,
                }))
              }
              options={PROVINCIAL_LEADING_INDUSTRY_OPTIONS.map((t) => ({
                label: t,
                value: t,
              }))}
            />
          </>
        )}
      </div>

      <div className="dash-section dash-section--grow">
        <div className="dash-field-label">
          {filter.sortBy === 'energy-asc'
            ? '园区列表（综合能源消费量：从小到大）'
            : filter.sortBy === 'energy-desc'
              ? '园区列表（综合能源消费量：从大到小）'
              : filter.sortBy === 'green-electricity-asc'
                ? '园区列表（绿电资源规模：从小到大）'
                : filter.sortBy === 'green-electricity-desc'
                  ? '园区列表（绿电资源规模：从大到小）'
                  : filter.sortBy === 'gdp-asc'
                    ? '园区列表（地区生产总值：从小到大）'
                    : filter.sortBy === 'gdp-desc'
                      ? '园区列表（地区生产总值：从大到小）'
                      : '园区列表（省 → 市 → 区 → 名称）'}
        </div>
        <div className="dash-list-wrap">
          <List
            size="small"
            dataSource={keywordFilteredParks}
            locale={{ emptyText: '暂无园区数据' }}
            renderItem={(park) => {
              const isSelected = selectedParkId === park.id;
              const sortValue = getParkSortValue(park, filter.sortBy);
              return (
                <List.Item
                  className={
                    isSelected ? 'dash-list-item dash-list-item--active' : 'dash-list-item'
                  }
                  onClick={() => onSelectPark(park.id)}
                >
                  <span className="dash-list-item__name">{park.name}</span>
                  {sortValue && (
                    <span className="dash-list-item__sort-value">{sortValue}</span>
                  )}
                </List.Item>
              );
            }}
          />
        </div>
      </div>
    </aside>
  );
}
